import {
  CompanyDevicePolicyStatus,
  CreateUserRequest,
  OrganizationWithUserGroupsResponse,
  UpdateUserRequest,
  UserGroupRequest,
  UserGroupResponse,
  UserGroupWithUsersResponse,
  UserListUser,
  UserPoliciesResponseSingleDevicePolicyEnum,
  UserResponse,
} from '@cdaas/api-clients/dist/asset-admin-openapi';
import { User } from '@cdaas/api-clients/dist/order-openapi';
import { UserWithOrganizationAndUserGroupResponse } from '@cdaas/api-clients/dist/asset-user-openapi';

export const getUserGroupRequestToCreateNewUserGroup = ({ name, default_user_roles }: UserGroupResponse): UserGroupRequest => ({
  name,
  default_user_roles,
});

export const getUserGroupRequestToUpdateExistingUserGroup = (userGroup: UserGroupResponse): UserGroupRequest => {
  return {
    name: userGroup.name,
    default_user_roles: userGroup.default_user_roles,
    policies: userGroup.policies,
    invoicing_account: userGroup.invoicing_account?.id,
    default_billing_address: userGroup.default_billing_address,
    default_billing_contact: userGroup.default_billing_contact,
    default_delivery_address: userGroup.default_delivery_address,
    default_delivery_contact: userGroup.default_delivery_contact,
  };
};

export const getUserUpdateRequest = ({
  phone_number,
  allowed_procurements,
  policies,
  user_roles,
  allow_access_without_bank_id,
}: UserResponse): UpdateUserRequest | undefined => {
  const userUpdate: UpdateUserRequest | undefined = phone_number
    ? {
        phone_number,
        user_roles: user_roles.user,
        allowed_procurements,
        allow_access_without_bank_id,
      }
    : undefined;

  if (!!userUpdate && policies?.lease_procurement_limit !== undefined) {
    userUpdate.policies = { lease_procurement_limit: policies.lease_procurement_limit };
  }
  return userUpdate;
};

export const getUserRequest = ({
  phone_number,
  email,
  allowed_procurements,
  user_roles,
  allow_access_without_bank_id,
}: UserResponse): CreateUserRequest | undefined => {
  return phone_number
    ? {
        phone_number,
        email,
        user_roles: user_roles.user,
        allowed_procurements,
        allow_access_without_bank_id,
      }
    : undefined;
};

export const getUserResponseFromUserListUser = (
  {
    id,
    email,
    phone_number,
    user_roles,
    given_name,
    family_name,
    allowed_procurements,
    name,
    invoicing_account,
    employee_id,
    allow_access_without_bank_id,
  }: UserListUser,
  userGroup: UserGroupWithUsersResponse,
  customerOrganization: OrganizationWithUserGroupsResponse,
): UserWithOrganizationAndUserGroupResponse => ({
  id,
  email,
  phone_number: phone_number || '',
  user_roles,
  given_name,
  family_name,
  employee_id,
  allowed_procurements,
  organization: customerOrganization,
  policies: {
    effective_lease_procurement_limit: null,
    lease_procurement_limit: null,
    single_device_policy: UserPoliciesResponseSingleDevicePolicyEnum.Disabled,
    company_device_policy: CompanyDevicePolicyStatus.NotNeeded,
  },
  allowance: null,
  allowance_type: null,
  invoicing_account: invoicing_account,
  inherited_invoicing_account: {
    name: '123456',
  },
  name: name,
  phone_number_verified: false,
  user_group: userGroup,
  allow_access_without_bank_id,
  party_id: null,
  prescheduled_onboarding_at: null,
  offboarding_at: null,
});

export const getUserFromUserResponse = ({ id, given_name, family_name, email, phone_number }: UserResponse): User | undefined =>
  id
    ? {
        user_id: id,
        given_name: given_name || undefined,
        family_name: family_name || undefined,
        email,
        phone_number,
      }
    : undefined;

export const getInitUser = (): UserResponse => {
  return {
    id: '',
    allowance: null,
    allowance_type: null,
    email: '',
    allowed_procurements: [],
    family_name: '',
    given_name: '',
    invoicing_account: null,
    inherited_invoicing_account: {
      name: '',
    },
    name: '',
    phone_number: '',
    phone_number_verified: false,
    employee_id: null,
    policies: {
      effective_lease_procurement_limit: null,
      lease_procurement_limit: null,
      single_device_policy: UserPoliciesResponseSingleDevicePolicyEnum.Disabled,
      company_device_policy: CompanyDevicePolicyStatus.NotNeeded,
    },
    user_roles: {
      effective: [],
      user: [],
      user_group: [],
    },
    allow_access_without_bank_id: false,
    party_id: null,
    prescheduled_onboarding_at: null,
    offboarding_at: null,
  };
};

export interface UserListUserWithUserGroup extends UserListUser {
  user_group: UserGroupResponse;
}

export const flattenOrganizationUsers = (organizationResponse: OrganizationWithUserGroupsResponse): UserListUserWithUserGroup[] => {
  return (
    organizationResponse.user_groups?.map(userGroup => {
      if (userGroup.id) {
        return (
          userGroup.users?.map(user => ({
            ...user,
            user_group: userGroup,
          })) || []
        );
      } else return [];
    }) || []
  ).flat();
};

export const deleteAllDefaultAddressesFromUserGroupRequestIfEmpty = (userGroup: UserGroupRequest) => {
  const delivery = userGroup.default_delivery_address;
  const billing = userGroup.default_billing_address;

  if (!delivery?.street && !delivery?.city && !delivery?.zip && !delivery?.country) {
    delete userGroup.default_delivery_address;
  }
  if (!billing?.street && !billing?.city && !billing?.zip && !billing?.country) {
    delete userGroup.default_billing_address;
  }
  return userGroup;
};
